import { v4 as uuid } from 'uuid';
import { lowerCase } from 'lodash';
import {
  ReplacementLibraryStoreItem,
  ReplacementsLibrary,
  ReplacementsLibraryStore,
} from '@replacements/store/useReplacementsLibraryStore';
import { ReplacementsLibraryListItem } from '@replacements/components/ReplacementsLibrary/List/ReplacementsLibraryList';
import {
  ALL_TERMS,
  NUMERICAL_TERMS,
} from '@replacements/components/ReplacementsLibrary/Toolbar/ReplacementsLibrarySortToolbar';
import { PhoneticReplacement } from '../../../../server/graphql/__generated.types';

/**
 * Group replacements by word
 */
export const aggregateReplacementsByWord = (
  replacements: Array<PhoneticReplacement>
): Array<ReplacementsLibraryListItem> => {
  const libraryEntries = new Map<string, ReplacementsLibraryListItem>();

  replacements.forEach(item => {
    const entry = libraryEntries.get(item.text);
    if (entry) {
      libraryEntries.set(item.text, {
        id: entry.id,
        text: entry.text,
        replacements: [...entry.replacements, item],
      });
    } else {
      libraryEntries.set(item.text, {
        id: uuid(),
        text: item.text,
        replacements: [item],
      });
    }
  });

  const library = Array.from(libraryEntries.entries()).map(item => item[1]);
  library.sort(sortFunc);

  return library;
};

/**
 * Creates a new word entry with it's first replacement in the library
 */
export const insertToLibrary = (
  library: ReplacementsLibrary,
  phoneticReplacement: PhoneticReplacement,
  context?: Parameters<ReplacementsLibraryStore['addReplacement']>[1]
) => {
  const updated = [...library];
  updated.push({
    id: uuid(),
    expanded: context?.type !== 'bulk',
    text: phoneticReplacement.text,
    replacements: [
      {
        id: phoneticReplacement.id,
        enabled: true,
        text: phoneticReplacement.text,
        replacement: phoneticReplacement.replacement,
        isRespelling: phoneticReplacement.isRespelling,
        userId: phoneticReplacement.userId,
        updatedAt: phoneticReplacement.updatedAt,
      },
    ],
    selected: true,
  });

  updated.sort(sortFunc);

  return updated;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sortFunc = (a: any, b: any) => {
  if (lowerCase(a.text) < lowerCase(b.text)) return -1;
  if (lowerCase(a.text) === lowerCase(b.text)) return 0;
  return 1;
};

const NUMERICAL_REGEX = /^[^a-zA-Z]/;

const wordStartsWith = (
  item: ReplacementLibraryStoreItem,
  filterValue: string
) => {
  return item.text.toLowerCase().startsWith(filterValue.toLowerCase());
};

export const filterFunc = (
  replacements: ReplacementsLibrary,
  filterValue: string
) => {
  if (filterValue === NUMERICAL_TERMS) {
    return replacements.filter(item => item.text.match(NUMERICAL_REGEX));
  }
  return replacements.filter(item => wordStartsWith(item, filterValue));
};

/**
 * Return true if a word has been added to library that matches filter value
 */
export const assertFilterMatch = (
  previousLibrary: ReplacementsLibrary,
  library: ReplacementsLibrary,
  filter: string
): boolean => {
  if (filter === ALL_TERMS) return false;

  const diff = library.filter(x => !previousLibrary.find(i => i.id === x.id));

  if (diff.length > 0) {
    let matchesFilter = false;
    if (filter === NUMERICAL_TERMS) {
      matchesFilter = diff.every(item => item.text.match(NUMERICAL_REGEX));
    } else {
      matchesFilter = diff.every(item => wordStartsWith(item, filter));
    }
    return matchesFilter;
  }
  return false;
};

export default {
  aggregateReplacementsByWord,
};
